// frontend/src/livequiz/useLiveQuizConnection.js
import { useRef, useState, useCallback, useEffect } from 'react';
import API_BASE_URL from '../quiz_management/config';

const WS_BASE_URL = API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');

/**
 * Shared WebSocket connection logic for live quiz student screens.
 * Handles connect, reconnect-safe cleanup, and generic message dispatch.
 * Mode-specific components pass an onMessage handler to process events.
 */
export function useLiveQuizConnection(studentId, onMessage) {
    const wsRef = useRef(null);
    const sessionCodeRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimer = useRef(null);
    const intentionalClose = useRef(false);
    const [connected, setConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);

    // Always dispatch to the latest handler (avoids stale-closure bugs
    // where history entries captured an outdated currentQuestion).
    const onMessageRef = useRef(onMessage);
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    const connect = useCallback((sessionCode) => {
        sessionCodeRef.current = sessionCode;
        intentionalClose.current = false;

        const ws = new WebSocket(`${WS_BASE_URL}/livequiz/ws/student/${sessionCode}/${studentId}`);
        wsRef.current = ws;

        ws.onopen = () => {
            setConnected(true);
            setConnectionError(null);
            reconnectAttempts.current = 0;
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            onMessageRef.current(data);
        };

        ws.onerror = () => {
            // onclose always fires after onerror; reconnection handled there
        };

        ws.onclose = () => {
            setConnected(false);
            if (intentionalClose.current) return;
            if (reconnectAttempts.current < 10) {
                const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 10000);
                reconnectAttempts.current += 1;
                setConnectionError('Connection lost. Reconnecting…');
                reconnectTimer.current = setTimeout(
                    () => connect(sessionCodeRef.current),
                    delay
                );
            } else {
                setConnectionError('Connection lost. Please refresh the page.');
            }
        };
    }, [studentId]);

    const send = useCallback((payload) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload));
            return true;
        }
        setConnectionError('Not connected. Reconnecting — please try again in a moment.');
        return false;
    }, []);

    useEffect(() => {
        return () => {
            intentionalClose.current = true;
            if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
            if (wsRef.current) wsRef.current.close();
        };
    }, []);

    return { connect, send, connected, connectionError };
}

export async function joinLiveQuizSession(sessionCode, studentId) {
    const res = await fetch(
        `${API_BASE_URL}/livequiz/session/${sessionCode.toUpperCase()}/join?student_id=${studentId}`,
        { method: 'POST' }
    );
    const data = await res.json();
    if (!res.ok) {
        const message = typeof data.detail === 'string' ? data.detail : 'Failed to join session';
        throw new Error(message);
    }
    return data;
}